import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { Button } from "./Button";
import { GameIcon } from "./GameIcon";
import { GameMark } from "./GameMark";

/**
 * THE ONE PRE-GAME SHELL — felt, header, centred card, «شروع بازی» — shared by ALL EIGHT solo
 * start screens: حکم, بی‌دل, پاسور, بلوف, شلم (through {@link SoloStartScreen}) and شطرنج, تخته نرد,
 * منچ directly. Each of the four used to carry its own copy of this markup, which is how the card
 * games came to look different from the board games.
 *
 * ⚠️ **THE SIZING HERE IS A BUDGET, NOT A STYLE.** The screen must fit at 320×568 with nothing
 * scrolled off, and حکم — the densest, with eight blocks — measured 736 px against a 484 px budget
 * before this pass. What bought the 252 px back, measured on the deployed build:
 *
 *   emoji INLINE with the heading (75 + 20 gap + 28 → one ~30 px row)   93 px
 *   row gap 20 → 12 (×7 on حکم)                                        56 px
 *   card padding 32 → 16                                               32 px
 *   compact deck previews, difficulty + match-length controls          ~60 px
 *   row labels 14 → 13 px and their inner gap 8 → 6                     ~6 px
 *
 * So: adding a row to any of these cards spends a real budget. Re-measure at 320×568
 * (`apps/web/tools/solo-start-probes/probe-compose.cjs`) before adding one.
 *
 * ⚠️ **IT STILL SCROLLS, AND THAT SAFETY NET STAYS.** A centred grid item that overflows is clipped
 * at BOTH ends — the heading becomes unreachable, with no way out. This is a flex box with
 * `overflow-y: auto` and an `auto` margin (which resolves to zero when free space is negative, so
 * the card lands at the top and the rest scrolls in). Fitting is the goal; scrolling is the floor.
 *
 * ⚠️ **BOTH SAFE-AREA INSETS GO THROUGH `max(env(...), var(--tg-safe-*))`.** Telegram's fullscreen
 * zeroes the CSS `env()` values and publishes the real insets on `<html>` instead, so a bare
 * `env()` is 0 on exactly the devices that have a navigation bar.
 *
 * `before` — cards of their OWN above the card, each a separate action that is not one of the card's
 * settings («سودوکو»'s unfinished game and its daily puzzle). Unset, nothing changes: the card alone,
 * centred exactly as before.
 */
export function StartScreenShell({
  title,
  emoji,
  heading,
  leaveLabel,
  startLabel = fa.soloStart.startGame,
  onStart,
  onLeave,
  before,
  children,
}: {
  /** The game's name, in the header (`fa.<game>.title`). */
  title: string;
  /** The game's badge, inline with the heading (`GAMES[].icon`, except حکم's own 🃏). */
  emoji: string;
  /** What the card asks — حکم's «بازی تا چند دست؟», or the shared «قبل از شروع». */
  heading: string;
  /** Accessible name for the exit control (`fa.<game>.leave`). */
  leaveLabel: string;
  startLabel?: string;
  onStart: () => void;
  onLeave: () => void;
  /** Cards of their own above the card (separate actions), stacked with it and centred as one. */
  before?: ReactNode;
  /** The game's own pre-game controls. */
  children: ReactNode;
}): ReactNode {
  const card = (
    <div
      className="end-card"
      style={{
        margin: before ? 0 : "auto",
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        borderRadius: "var(--radius-lg)",
        padding: "12px 14px",
        width: "100%",
        maxWidth: 340,
        textAlign: "center",
        boxShadow: "0 24px 60px rgba(0,0,0,.4)",
        display: "flex",
        flexDirection: "column",
        gap: 9,
        alignItems: "center",
      }}
    >
      {/* The badge sits ON the heading's line. Stacked, the two cost 123 px of a 484 px budget
          at 320×568 — a sixth of the whole card for a decoration and four words. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          width: "100%",
        }}
      >
        <span style={{ fontSize: 22, lineHeight: 1.1 }} aria-hidden>
          <GameMark icon={emoji} />
        </span>
        <span style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.3 }}>{heading}</span>
      </div>

      {children}

      <Button variant="primary" onClick={onStart} style={{ width: "100%" }}>
        {startLabel}
      </Button>
    </div>
  );
  return (
    <div
      style={{
        position: "relative",
        height: "var(--app-h)",
        display: "flex",
        flexDirection: "column",
        // The SAME felt the Boot screen uses, so picker → connecting → table is one continuous
        // table rather than a flash of a different background.
        background:
          "radial-gradient(130% 120% at 50% 30%, var(--felt-2) 0%, var(--felt-edge) 55%, var(--boot-vignette) 100%)",
        color: "var(--text)",
      }}
    >
      {/* ABSOLUTE header — it consumes no flex height, so the card centres on the space below it
          rather than being pushed down by half a header. */}
      <div
        className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-4"
        style={{
          paddingTop: "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
        }}
      >
        <button
          type="button"
          onClick={onLeave}
          aria-label={leaveLabel}
          className="appearance-none m-0 cursor-pointer flex h-10 w-10 flex-none items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95"
        >
          <GameIcon type="exit" size={20} />
        </button>
        <span className="font-headline-md text-headline-md font-bold text-secondary">{title}</span>
        <span className="h-10 w-10 flex-none" />
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          overflowY: "auto",
          // 52 px clears the absolute header (which ends at 50 px + the top inset). The card is
          // centred by the `auto` margin below, in the band this padding leaves.
          padding:
            "calc(52px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px))) 20px" +
            " calc(24px + max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)))",
        }}
      >
        {before ? (
          <div
            style={{
              margin: "auto",
              width: "100%",
              maxWidth: 340,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {before}
            {card}
          </div>
        ) : (
          card
        )}
      </div>
    </div>
  );
}

/** A labelled full-width row on a start card. The ONE row idiom all eight screens use. */
export function StartRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--hint)", textAlign: "start" }}>
        {label}
      </div>
      {children}
    </div>
  );
}
